import REGL, { TextureImageData } from "regl";

import { Bounds, TerrainCanvasInfo } from "../types";

import demFrag from "./shaders/dem.frag";
import demVert from "./shaders/dem.vert";
import renderFrag from "./shaders/render.frag";

type CalcateOverlapType = {
  width: number;
  height: number;
  bounds: Bounds;
  windImage: CanvasImageSource | Uint8Array;
  terrainCanvasInfo?: TerrainCanvasInfo | null;
};

// We don't want to show any 2D flow where there isn't a DEM. Thats what we do here. We also blur the image to make cells look like they flow over in one another
export const caluclateOverlap = ({
  width,
  height,
  bounds,
  windImage,
  terrainCanvasInfo,
}: CalcateOverlapType) => {
  if (!terrainCanvasInfo) return null;

  const createOffscreenCanvas = (): [OffscreenCanvas, CanvasRenderingContext2D] => {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext("2d") as unknown as CanvasRenderingContext2D;
    return [canvas, ctx];
  };

  const canvas = document.createElement("canvas");
  const regl = REGL({ canvas, extensions: ["OES_texture_float"] });
  canvas.width = width;
  canvas.height = height;

  const { imageDEM, terrainBounds } = terrainCanvasInfo;

  const [DemCanvas, DemCtx] = createOffscreenCanvas();

  // Calculate what part of the canvas is needed
  const totalDistanceX = terrainBounds[2] - terrainBounds[0];
  const totalDistanceY = terrainBounds[1] - terrainBounds[3];
  const distanceLeft = bounds[0] - terrainBounds[0];
  const distanceTop = terrainBounds[1] - bounds[1];
  const distanceRight = bounds[2] - terrainBounds[2];
  const distanceBottom = terrainBounds[3] - bounds[3];

  const xPoint = (distanceLeft * imageDEM.width) / totalDistanceX;
  const yPoint = (distanceTop * imageDEM.height) / totalDistanceY;
  const xPointEnd = (distanceRight * imageDEM.width) / totalDistanceX;
  const yPointEnd = (distanceBottom * imageDEM.height) / totalDistanceY;

  DemCtx?.drawImage(
    imageDEM as CanvasImageSource,
    xPoint,
    yPoint,
    Math.abs(xPointEnd + imageDEM.width - xPoint),
    Math.abs(yPointEnd + imageDEM.height - yPoint),
    0,
    0,
    width,
    height
  );

  // Elevation texture
  const tDem = regl.texture({
    data: DemCanvas as unknown as TextureImageData,
    flipY: true,
  });

  const [windCanvas, windCtx] = createOffscreenCanvas();
  if (windCtx) {
    windCtx.filter = "blur(2.5px)";
    windCtx.drawImage(windImage as CanvasImageSource, 0, 0);
  }

  // Elevation texture
  const tWindData = regl.texture({
    data: windCanvas as unknown as TextureImageData,
    flipY: true,
  });

  // Elevation framebuffer
  const fboElevation = regl.framebuffer({
    width: canvas.width,
    height: canvas.height,
    colorType: "float",
  });

  const defaultReglProps = {
    vert: demVert,
    uniforms: {
      resolution: [width, height],
    },
    viewport: {
      x: 0,
      y: 0,
      width: width,
      height: height,
    },
    attributes: {
      position: [-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1],
    },
    count: 6,
  };

  // Draw to the framebuffer
  regl({
    ...defaultReglProps,
    frag: demFrag,
    framebuffer: fboElevation,
    uniforms: {
      ...defaultReglProps.uniforms,
      tWindData: tWindData,
      tDem: tDem,
    },
  })();

  // Render the framebuffer to the canvas element
  regl({
    ...defaultReglProps,
    frag: renderFrag,
    uniforms: {
      ...defaultReglProps.uniforms,
      texture: fboElevation,
    },
  })();

  regl.destroy();

  return canvas;
};
